/**
 * @file useSitePreviewToken.ts
 * @description Токен предпросмотра черновика или версии (phase19.16 Р1;
 * контракт §7 `POST /admin/site-view/preview-token`, срок 1 час).
 *
 * С `path` ключ сразу ведёт на нужный адрес витрины (контракт §14.1).
 * Токен запрашивается при первом показе и заново, когда до конца срока
 * осталось меньше минуты (окно предпросмотра могли держать открытым весь
 * день). Ошибка — текст «Предпросмотр сейчас недоступен…» и `retry`.
 */

import { useCallback, useEffect, useState } from "react";

import { siteView } from "../../lib/api-site-view";
import { siteViewErrorText } from "../../lib/site-view/errors";
import type { SitePreviewSource, SitePreviewToken } from "../../lib/site-view/types";

const UNAVAILABLE =
  "Предпросмотр сейчас недоступен. Настройки можно менять — черновик сохраняется. Проверьте вид позже или после публикации.";
/** Запас до конца срока токена, мс. */
const RENEW_BEFORE_MS = 60_000;

export interface PreviewTokenState {
  token: SitePreviewToken | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/** Ключ источника для зависимостей эффекта (объект источника пересоздаётся на каждом рендере). */
export function previewSourceKey(source: SitePreviewSource): string {
  if (source.source === "draft") return "draft";
  return source.source === "revision" ? `rev:${source.version}` : `design:${source.designId}`;
}

/** `path` — куда войти на витрине (канва страницы — `/preview/<slug>`); нет — главная. */
export function useSitePreviewToken(source: SitePreviewSource | null, path?: string): PreviewTokenState {
  const [token, setToken] = useState<SitePreviewToken | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = source ? `${previewSourceKey(source)}|${path ?? ""}` : null;

  useEffect(() => {
    if (!key || !source) return;
    let cancelled = false;
    let renew: ReturnType<typeof setTimeout> | null = null;
    setLoading(true);
    setError(null);
    siteView
      .previewToken(source, path)
      .then((next) => {
        if (cancelled) return;
        setToken(next);
        const left = new Date(next.expiresAt).getTime() - Date.now() - RENEW_BEFORE_MS;
        if (Number.isFinite(left) && left > 0) renew = setTimeout(() => setAttempt((a) => a + 1), left);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(siteViewErrorText(err, UNAVAILABLE));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (renew) clearTimeout(renew);
    };
    // source пересоздаётся на каждом рендере — следим за ключом.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { token, loading, error, retry };
}
